// Two-pane review surface for the verbal-authoring modal. Owns the
// chat history + refine-endpoint round-trips on the left, and the
// draft editor + kind feedback on the right. Pulled out of the modal
// shell so the shell stays under the file-size budget and so the
// review surface can be unit-tested independently.
//
// Plan reference: `docs/planning/mfg_recipe_verbal_authoring.md` §3.4.

import { useCallback, useMemo, useState } from "react";
import { mfgPrimaryBtnLgClass, mfgSecondaryBtnClass } from "@/components/manufacturing/manufacturingSurfaceClasses";
import { summarizeDictatedRecipeDraftDiff } from "@/lib/manufacturing/dictatedRecipeDraftDiff";
import { ensureFullBomReplacementDraft } from "@/lib/manufacturing/ensureFullBomReplacementDraft";
import {
  correctBomFromTranscript,
  refineDictatedRecipeDraft,
} from "@/lib/manufacturing/recipesDictationClient";
import { normalizeSlotLabel } from "@/lib/manufacturing/slotLabel";
import type { DictatedDraftChatTurn, DictatedRecipeDraft } from "@/lib/manufacturing/types/recipeDictation";
import type { MfgMaterial, MfgRecipeKind } from "@/lib/manufacturing/types";
import { ManufacturingBomCorrectionDiffPanel } from "./ManufacturingBomCorrectionDiffPanel";
import { ManufacturingDictatedRecipeDraftEditor } from "./ManufacturingDictatedRecipeDraftEditor";
import { ManufacturingDictatedRecipeKindFeedback } from "./ManufacturingDictatedRecipeKindFeedback";
import { ManufacturingDictatedRecipeRefineChat } from "./ManufacturingDictatedRecipeRefineChat";

function normalizeDraftSlots(draft: DictatedRecipeDraft): DictatedRecipeDraft {
  return {
    ...draft,
    lines: draft.lines.map((ln) => ({
      ...ln,
      slot_label: normalizeSlotLabel(ln.slot_label),
    })),
  };
}

const SEED_ASSISTANT_TEXT =
  "I drafted this recipe from your dictation. Edit anything on the right, or describe changes here and I'll apply them.";

const BOM_CORRECT_SEED_TEXT =
  "Tell me the formula or edits (slot labels + math). I'll update quantities and can add materials. Review the lines on the right before saving.";

function turnId(): string {
  return `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
}

function seedTurn(mode: "create" | "bom_correct"): DictatedDraftChatTurn {
  return {
    id: "seed",
    role: "assistant",
    text: mode === "bom_correct" ? BOM_CORRECT_SEED_TEXT : SEED_ASSISTANT_TEXT,
    diffSummary: null,
    createdAt: new Date().toISOString(),
  };
}

export function ManufacturingDictatedRecipeReview({
  transcript,
  draft,
  priorDraft = null,
  onDraftChange,
  onRedictate,
  onClose,
  onSave,
  saveBusy,
  saveError,
  materials,
  orgSlotLabels = [],
  fallback,
  mode = "create",
  saveLabel = "Save recipe",
  redictateLabel = "Re-dictate",
}: {
  transcript: string;
  draft: DictatedRecipeDraft;
  /** Seeded BOM before correction — enables before/after diff in bom_correct mode. */
  priorDraft?: DictatedRecipeDraft | null;
  onDraftChange: (next: DictatedRecipeDraft) => void;
  onRedictate: () => void;
  onClose: () => void;
  onSave: () => void;
  saveBusy: boolean;
  saveError: string | null;
  materials: ReadonlyArray<MfgMaterial>;
  orgSlotLabels?: readonly string[];
  fallback?: boolean;
  mode?: "create" | "bom_correct";
  saveLabel?: string;
  redictateLabel?: string;
}) {
  const [turns, setTurns] = useState<DictatedDraftChatTurn[]>(() => [seedTurn(mode)]);
  const [submitting, setSubmitting] = useState(false);
  const [refineError, setRefineError] = useState<string | null>(null);

  const canSave =
    !saveBusy &&
    !submitting &&
    draft.name.trim().length > 0 &&
    draft.lines.length > 0;

  const handleKindChange = useCallback(
    (kind: MfgRecipeKind) => onDraftChange({ ...draft, kind }),
    [draft, onDraftChange],
  );

  const handleSendTurn = useCallback(
    async (text: string) => {
      if (submitting) return;
      setRefineError(null);
      const userTurn: DictatedDraftChatTurn = {
        id: turnId(),
        role: "user",
        text,
        diffSummary: null,
        createdAt: new Date().toISOString(),
      };
      setTurns((prev) => [...prev, userTurn]);
      setSubmitting(true);

      const turnPrior = draft;
      const result =
        mode === "bom_correct"
          ? await correctBomFromTranscript({ priorDraft: turnPrior, transcript: text })
          : await refineDictatedRecipeDraft({
              transcript,
              priorDraft: turnPrior,
              turn: text,
            });

      setSubmitting(false);

      if (!result.ok || !result.data.ok || !result.data.draft) {
        const errMsg = !result.ok
          ? result.error || "Refinement failed."
          : "Refinement could not produce a usable draft.";
        setRefineError(errMsg);
        setTurns((prev) => [
          ...prev,
          {
            id: turnId(),
            role: "assistant",
            text: `Couldn't apply that change: ${errMsg}`,
            diffSummary: null,
            createdAt: new Date().toISOString(),
          },
        ]);
        return;
      }

      const newDraftRaw = normalizeDraftSlots(result.data.draft);
      const newDraft =
        mode === "bom_correct"
          ? ensureFullBomReplacementDraft(turnPrior, newDraftRaw)
          : newDraftRaw;
      const diffSummary = summarizeDictatedRecipeDraftDiff(turnPrior, newDraft);
      onDraftChange(newDraft);
      setTurns((prev) => [
        ...prev,
        {
          id: turnId(),
          role: "assistant",
          text: "Updated the draft.",
          diffSummary,
          createdAt: new Date().toISOString(),
        },
      ]);
    },
    [draft, mode, onDraftChange, submitting, transcript],
  );

  const cappedTurns = useMemo(() => turns, [turns]);

  return (
    <div className="space-y-3">
      {fallback ? (
        <div className="rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800 dark:border-amber-700 dark:bg-amber-950/35 dark:text-amber-200">
          Extractor output was malformed; this is a fallback draft. Re-dictate or
          hand-edit the fields below before saving.
        </div>
      ) : null}

      <div className="grid gap-3 md:grid-cols-2">
        <div className="md:col-span-1">
          <ManufacturingDictatedRecipeRefineChat
            turns={cappedTurns}
            submitting={submitting}
            onSendTurn={(text) => void handleSendTurn(text)}
          />
          {refineError ? (
            <div className="mt-2 text-xs text-red-600 dark:text-red-400">
              {refineError}
            </div>
          ) : null}
        </div>
        <div className="md:col-span-1 space-y-3">
          {mode === "create" ? (
            <ManufacturingDictatedRecipeKindFeedback
              currentKind={draft.kind}
              detectedKind={draft.detectedKind}
              onKindChange={handleKindChange}
            />
          ) : (
            <>
              <div className="rounded-md border border-violet-200 bg-violet-50 px-3 py-2 text-xs text-violet-900 dark:border-violet-800 dark:bg-violet-950/40 dark:text-violet-100">
                Saving <strong>replaces</strong> all material lines with the proposed list below.
                Slot dropdown uses suggestions only (no chip list). Nested component / part-use
                lines stay as-is.
              </div>
              {priorDraft ? (
                <ManufacturingBomCorrectionDiffPanel prior={priorDraft} next={draft} />
              ) : null}
            </>
          )}
          <ManufacturingDictatedRecipeDraftEditor
            draft={draft}
            onChange={onDraftChange}
            materials={materials}
            orgSlotLabels={orgSlotLabels}
          />
        </div>
      </div>

      {saveError ? (
        <div className="text-xs text-red-600 dark:text-red-400">{saveError}</div>
      ) : null}

      <div className="flex justify-end gap-2">
        <button
          type="button"
          className={mfgSecondaryBtnClass}
          onClick={onClose}
          disabled={saveBusy}
        >
          Cancel
        </button>
        <button
          type="button"
          className={mfgSecondaryBtnClass}
          onClick={onRedictate}
          disabled={saveBusy}
        >
          {redictateLabel}
        </button>
        <button
          type="button"
          className={mfgPrimaryBtnLgClass}
          onClick={onSave}
          disabled={!canSave}
          title={
            !canSave && !saveBusy
              ? "Recipe needs a name and at least one line before saving."
              : undefined
          }
        >
          {saveBusy ? "Saving…" : saveLabel}
        </button>
      </div>
    </div>
  );
}
